import { useEffect, useMemo, useState } from 'react';
import './accounting.css';
import '../modules/SettingsDetailPage.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AiCombo } from '../../components/AiCombo';
import { AppToolbar, ExportMenu } from '../../components/AppToolbar';
import { AppDialog } from '../../components/AppDialog';
import { FormTitle } from '../../components/FormTitle';
import { ViewToggle } from '../../components/ViewToggle';

import { useNotify } from '../../components/notifications';
import { useConfirm, useReasonGate } from '../../components/confirm';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { useUnsavedGuard, confirmDiscardIfUnsaved } from '../../hooks/useUnsavedGuard';
import { runWithAuditReason } from '../../api/client';
import { useFetchMs } from '../../hooks/useFetchMs';
import {
  usePaymentTermsSetup, usePaymentTerms, useSetCustomerPaymentTerm, useSavePaymentTerm, errMessage,
  type PaymentTermsSetupRow,
} from './accounting';

interface Props { onHome?: () => void; }

/**
 * Settings ▸ Accounting ▸ Payment Terms Setup (top-level Accounting node — the
 * per-client grid; SEPARATE from Utility ▸ Accounting which holds the lookup).
 * Mirrors the legacy ALPACA `Customer_ListView_PaymentTermsSetup`: a grid of
 * clients where Client / Address / Phone are read-only and only ClientCode + the
 * Terms (a PaymentTerms `Customer.Terms` FK) are editable. No New / Delete
 * (assignment only), exactly like ALPACA.
 *
 * List / Detail (owner request 2026-09-29): the List View is READ-ONLY — it shows
 * the clients only; DOUBLE-CLICKING a row (or the shared <ViewToggle> / Edit)
 * opens that client in the full-page DETAIL View where the assignment is edited
 * (standing rules 10 & 12). The list no longer edits inline.
 */
// onHome drives the "Home" link in the list-view breadcrumb (re-added 2026-10-14,
// owner request — AppShell passes it as () => open('home')).
export function PaymentTermsSetupPage({ onHome }: Props) {
  const notify = useNotify();
  const { confirm, alert } = useConfirm();
  const gateReason = useReasonGate();
  const { data: rows = [], isFetching, refetch } = usePaymentTermsSetup();
  const { data: options = [] } = usePaymentTerms();           // day-value lookup → dropdown
  const loadMs = useFetchMs(isFetching);
  const setTerm = useSetCustomerPaymentTerm();
  const saveTerm = useSavePaymentTerm();
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [gridMax, setGridMax] = useState(false);   // controlled grid maximize (search-row zoom button)

  // List ⇄ Detail. detailId = the client row being edited. detailMode is the
  // Detail View's sub-mode: 'view' opens READ-ONLY with an Edit button (standing
  // rule 12), 'edit' shows the Save trio.
  const [mode, setMode] = useState<'list' | 'detail'>('list');
  const [detailMode, setDetailMode] = useState<'view' | 'edit'>('view');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [draftTermsId, setDraftTermsId] = useState('');
  const [draftClientCode, setDraftClientCode] = useState('');

  // Inline "New payment term" popup (mirrors the legacy lookup's + button → the
  // small "Payment Terms" dialog with a numeric Terms field).
  const [showNewTerm, setShowNewTerm] = useState(false);
  const [newTermValue, setNewTermValue] = useState('');
  const [newTermError, setNewTermError] = useState('');

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.client, r.clientCode, r.address, r.phone, r.termDays != null ? `${r.termDays} days` : '']
        .some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [rows, search]);

  // LIMS (XAF / SQL Server) sorts these string columns LEXICALLY — plain culture
  // string compare (blanks first), NOT numeric-aware — unlike the grid's default.
  // Reproduce it so the grid AND the Detail prev/next walk the same order.
  const addressCmp = (a: PaymentTermsSetupRow, b: PaymentTermsSetupRow) => {
    const av = (a.address ?? '').trim(), bv = (b.address ?? '').trim();
    if (av === '' && bv === '') return 0;
    if (av === '') return -1;
    if (bv === '') return 1;
    return av.localeCompare(bv, undefined, { sensitivity: 'base' });
  };
  const ordered = useMemo(() => [...visible].sort(addressCmp), [visible]);

  const record = useMemo(
    () => (detailId ? (rows.find((r) => r.id === detailId) ?? null) : null),
    [rows, detailId]);
  const detailIndex = useMemo(
    () => (detailId ? ordered.findIndex((r) => r.id === detailId) : -1),
    [ordered, detailId]);

  const dirty = mode === 'detail' && detailMode === 'edit' && record != null
    && (draftTermsId !== (record.termsId ?? '') || draftClientCode !== (record.clientCode ?? ''));
  // Guard menu / sign-out / browser navigation while the detail draft is dirty (rule 15).
  useUnsavedGuard(dirty);

  // Esc restores from the maximized list grid (the page owns the maximize —
  // page-level chrome-hide, NOT AppDataGrid's fullscreen overlay — so the Restore
  // button in the search row stays visible, mirroring ModuleListView).
  useEffect(() => {
    if (!gridMax) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setGridMax(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [gridMax]);

  // ── List ⇄ Detail wiring ──
  const beginDetail = (row: PaymentTermsSetupRow, dmode: 'view' | 'edit') => {
    setDetailId(row.id);
    setDraftTermsId(row.termsId ?? '');
    setDraftClientCode(row.clientCode ?? '');
    setDetailMode(dmode);
    setMode('detail');
  };
  const openDetail = (id: string, dmode: 'view' | 'edit') => {
    const row = rows.find((r) => r.id === id);
    if (row) beginDetail(row, dmode);
  };
  /** ViewToggle / double-click → Detail: open the selected (or first) client
   *  READ-ONLY; the Edit button switches to editing (standing rule 12). */
  const toggleToDetail = () => {
    const sel = selectedIds.size === 1 ? String([...selectedIds][0]) : null;
    const target = sel ?? ordered[0]?.id ?? null;
    if (target) openDetail(target, 'view');
    else notify.warning('No clients to edit.');
  };

  // Route through the ONE shared unsaved-changes guard (rule 15) rather than a
  // bespoke confirm. Because the page registers useUnsavedGuard(dirty) above, the
  // global click-interceptor already catches the Cancel / Close / prev-next
  // buttons and prompts once; it then REPLAYS the click, which lands here — and
  // confirmDiscardIfUnsaved() sees the global `approved` flag set by that first
  // prompt and resolves silently, so the two layers can no longer BOTH ask (the
  // "popup warning twice" bug). Esc is a keypress, not an intercepted click, so it
  // still gets exactly one prompt from this call.
  const confirmDiscard = () => confirmDiscardIfUnsaved();
  const closeDetail = async () => { if (await confirmDiscard()) setMode('list'); };
  const goRel = async (delta: number) => {
    const target = ordered[detailIndex + delta];
    if (!target) return;
    if (!(await confirmDiscard())) return;
    beginDetail(target, detailMode);   // keep the current view/edit sub-mode
  };

  const saveDetail = async (after: 'stay' | 'close') => {
    if (!record) return;
    // Confirm (rule 7) → reason-for-change gate (rule 11; prompts only when
    // ReasonRequired is ON) → the existing per-client assignment endpoint.
    const ok = await confirm({ title: 'Save', message: 'Do you want to Save?' });
    if (!ok) return;
    const reason = await gateReason('Payment Terms Setup', 'edit');
    if (reason === null) return;
    setSaving(true);
    try {
      await runWithAuditReason(reason, () => setTerm.mutateAsync({
        customerId: record.id, termsId: draftTermsId || null, clientCode: draftClientCode.trim() || null,
      }));
      await alert({ title: 'Success', message: 'Saved successfully.', tone: 'success' });
      await refetch();
      if (after === 'close') setMode('list');
    } catch (e) { notify.error(errMessage(e)); }
    finally { setSaving(false); }
  };

  // New (the legacy lookup's + button → "Payment Terms" popup): create a new
  // PaymentTerms day-value, then auto-select it as the current draft.
  const openNewTerm = () => { setNewTermValue(''); setNewTermError(''); setShowNewTerm(true); };
  const createNewTerm = async () => {
    const n = Number(newTermValue);
    if (!newTermValue.trim() || !Number.isInteger(n) || n <= 0) {
      setNewTermError('Enter a whole number of days greater than 0.');
      return;
    }
    try {
      const res = await saveTerm.mutateAsync({ terms: n });
      const newId = (res as { data?: { data?: { id?: string } } })?.data?.data?.id;
      notify.success('Payment term added.');
      setShowNewTerm(false);
      if (newId) setDraftTermsId(newId);
    } catch (e) { notify.error(errMessage(e)); }
  };

  // Detail hotkeys (rule 14): view → Edit / Close; edit → Save · Save & Close ·
  // Cancel; both → prev / next record.
  const navHotkeys = [
    { combo: 'alt+left', label: 'Previous record', group: 'Payment Terms Setup', order: 6, disabled: detailIndex <= 0, run: () => void goRel(-1) },
    { combo: 'alt+right', label: 'Next record', group: 'Payment Terms Setup', order: 7, disabled: detailIndex < 0 || detailIndex >= ordered.length - 1, run: () => void goRel(1) },
  ];
  useHotkeys(
    mode !== 'detail'
      ? []
      : detailMode === 'view'
      ? [
          { combo: 'alt+e', label: 'Edit', group: 'Payment Terms Setup', order: 1, run: () => setDetailMode('edit') },
          { combo: 'f2', label: 'Edit', group: 'Payment Terms Setup', order: 1, hidden: true, run: () => setDetailMode('edit') },
          { combo: 'esc', label: 'Close', group: 'Payment Terms Setup', order: 2, allowInInput: false, run: () => void closeDetail() },
          ...navHotkeys,
        ]
      : [
          { combo: 'mod+s', label: 'Save', group: 'Payment Terms Setup', order: 1, run: () => void saveDetail('stay') },
          { combo: 'mod+enter', label: 'Save & Close', group: 'Payment Terms Setup', order: 2, run: () => void saveDetail('close') },
          { combo: 'esc', label: 'Cancel', group: 'Payment Terms Setup', order: 3, allowInInput: false, run: () => void closeDetail() },
          ...navHotkeys,
        ],
  );

  const columns: AppColumn<PaymentTermsSetupRow>[] = [
    // Per-row Edit pencil (owner request 2026-10-12) — opens that client in the Detail
    // View in EDIT mode directly. stopPropagation so the click doesn't also toggle the
    // row's selection or fire its double-click (which opens the read-only view). Same
    // borderless blue-pencil look as the ModuleListView row-edit pencil.
    {
      field: '__edit', header: '', width: 40, sortable: false, filterable: false,
      noExport: true, align: 'center', text: () => '',
      render: (r) => (
        <button
          type="button"
          className="pts-edit-pencil"
          title="Edit"
          aria-label={`Edit ${r.client ?? r.clientCode ?? 'client'}`}
          onClick={(e) => { e.stopPropagation(); openDetail(r.id, 'edit'); }}
        ><i className="ti ti-edit" aria-hidden="true" /></button>
      ),
    },
    { field: 'client',     header: 'Client',     minWidth: 220, sortable: false, text: (r) => r.client ?? '' },
    { field: 'clientCode', header: 'ClientCode', width: 120, minWidth: 100, sortable: false, text: (r) => r.clientCode ?? '' },
    { field: 'address',    header: 'Address',    minWidth: 180, text: (r) => r.address ?? '', compare: addressCmp },
    { field: 'phone',      header: 'Phone',      width: 150, minWidth: 120, sortable: false, text: (r) => r.phone ?? '' },
    {
      field: 'terms', header: 'Terms', width: 200, minWidth: 190, sortable: false,
      text: (r) => (r.termDays != null ? `${r.termDays} days` : ''),
    },
  ];

  // Export matrix for the search-row Export icon — the SAME visible rows/columns
  // the grid shows (shared gridExportMatrix helper).
  const exportMatrix = useMemo(() => gridExportMatrix(visible, columns), [visible, columns]);

  // "New payment term" popup — the ONE shared <AppDialog> (standing rules 7/9/12):
  // standard chrome, backdrop, sizing; it handles Esc-close, auto-focus of the first
  // control, Enter → footer primary (OK) and focus-return to the opener for free, so
  // the form supplies only the title / body / footer. (Was a bespoke acc-dialog.)
  const newTermPopup = (
    <AppDialog
      open={showNewTerm}
      title="Payment Terms"
      icon="ti-cash"
      width={420}
      className="pts-terms-dlg"
      onClose={() => setShowNewTerm(false)}
      footer={
        <>
          <button className="ca-btn" onClick={() => setShowNewTerm(false)}>
            <span className="tb-i tb-i-grey">↩︎</span> Cancel
          </button>
          <button className="ca-btn ca-btn-solid" onClick={() => void createNewTerm()} disabled={saveTerm.isPending}>
            <span className="tb-i">💾</span> {saveTerm.isPending ? 'Saving…' : 'OK'}
          </button>
        </>
      }
    >
      {/* Horizontal field — label beside the control (owner request 2026-10-12),
          the shared .acc-field-h layout (label left, input right on one line). */}
      <div className="acc-field-h">
        <label className="acc-label">Terms (days)<span className="req">*</span></label>
        <div className="acc-fh-control">
          <input className="acc-input" type="number" min={1} step={1} value={newTermValue}
            onChange={(e) => { setNewTermValue(e.target.value); if (newTermError) setNewTermError(''); }} />
          {newTermError && <div className="acc-err">{newTermError}</div>}
        </div>
      </div>
    </AppDialog>
  );

  // ── Detail View — the per-client editor (Client / Address / Phone read-only;
  //    ClientCode + Terms editable). All editing happens here (rule 12). ──
  if (mode === 'detail' && record) {
    const recordName = record.client?.trim() || record.clientCode?.trim() || 'Client';
    const viewOnly = detailMode === 'view';
    const Actions = ({ bottom }: { bottom?: boolean }) => (
      <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
        {viewOnly ? (
          <>
            <button className="sd-abtn primary" onClick={() => setDetailMode('edit')}
              title={`Edit (${formatCombo('alt+e')})`}><span className="tb-i tb-i-blue">✎</span> Edit</button>
            <button className="sd-abtn cancel" onClick={() => void closeDetail()}
              title={`Close (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Close</button>
          </>
        ) : (
          <>
            <button className="sd-abtn primary" disabled={saving} onClick={() => void saveDetail('close')}
              title={`Save & Close (${formatCombo('mod+enter')})`}><span className="tb-i">💾</span> Save &amp; Close</button>
            <button className="sd-abtn" disabled={saving} onClick={() => void saveDetail('stay')}
              title={`Save (${formatCombo('mod+s')})`}><span className="tb-i">💾</span> Save</button>
            <span className="sd-abtn-sep" aria-hidden="true" />
            <button className="sd-abtn cancel" disabled={saving} onClick={() => void closeDetail()}
              title={`Cancel (${formatCombo('esc')})`}><span className="tb-i">↩︎</span> Cancel</button>
          </>
        )}
      </div>
    );

    return (
      // pts-detail scopes the SQUARE-corner input override (owner request 2026-10-12,
      // THIS form only) — see accounting.css .pts-detail .sd-input / .ai-lkbox.
      <div className="pts-detail" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <div style={{ padding: '10px 16px 0', flex: '0 0 auto' }}>
          <ViewToggle view="detail" onChange={(v) => { if (v === 'list') void closeDetail(); }} />
        </div>
        <div className="bl-page mlv-host">
          <div className="bl-grid-area">
            <div className="sd-wrap">
              <div className="sd-head">
                <div>
                  <h2>Payment Terms Setup – {recordName}</h2>
                  <div className="sd-bc">
                    <a onClick={() => void closeDetail()}>Payment Terms Setup</a><i className="ti ti-chevron-right" />
                    <span>{recordName}</span>
                  </div>
                </div>
                {/* Prev / next record arrows walk the current (filtered, address-sorted)
                    list — shared SettingsDetailPage markup (.sd-navarrows / .sd-na). */}
                <div className="sd-navarrows">
                  <button className="sd-na" disabled={detailIndex <= 0} onClick={() => void goRel(-1)}
                    title={`Previous record (${formatCombo('alt+left')})`}>‹</button>
                  <button className="sd-na next" disabled={detailIndex < 0 || detailIndex >= ordered.length - 1} onClick={() => void goRel(1)}
                    title={`Next record (${formatCombo('alt+right')})`}>›</button>
                </div>
              </div>

              <Actions />

              <div className="sd-body">
                <h3 className="sd-section-h">Client Payment Terms</h3>
                <div className="sd-grid sd-grid--single sd-grid--side">
                  <div className="sd-row">
                    <label className="sd-label">Client</label>
                    <input className="sd-input readonly" value={record.client ?? ''} readOnly />
                  </div>
                  <div className="sd-row">
                    <label className="sd-label">ClientCode</label>
                    {/* ClientCode editable but WITHOUT the standard editable-yellow cue
                        (owner request 2026-10-12) — acc-input-plain forces white. View
                        mode keeps the read-only grey. */}
                    <input className={`sd-input${viewOnly ? ' readonly' : ' acc-input-plain'}`} value={draftClientCode}
                      readOnly={viewOnly} disabled={saving}
                      onChange={(e) => setDraftClientCode(e.target.value)} />
                  </div>
                  <div className="sd-row">
                    <label className="sd-label">Address</label>
                    <input className="sd-input readonly" value={record.address ?? ''} readOnly />
                  </div>
                  <div className="sd-row">
                    <label className="sd-label">Phone</label>
                    <input className="sd-input readonly" value={record.phone ?? ''} readOnly />
                  </div>
                  <div className="sd-row">
                    <label className="sd-label">Terms</label>
                    <AiCombo compact value={draftTermsId} disabled={viewOnly || saving}
                             options={options.map((o) => ({ value: o.id, label: `${o.terms} days` }))}
                             onChange={(v) => setDraftTermsId(v ?? '')}
                             onNew={openNewTerm} newTitle="New payment term" />
                  </div>
                </div>
              </div>

              <Actions bottom />
            </div>
          </div>
        </div>
        {newTermPopup}
      </div>
    );
  }

  // ── List View (READ-ONLY) ──
  return (
    <div className={`acc-page${gridMax ? ' acc-fullgrid' : ''}`}>
      {/* Navigator (breadcrumb) re-added ABOVE the List/Detail toggle (owner request
          2026-10-14), matching the Analysis Department Chain page. Below it, the
          Toggle + FormTitle share ONE flex row so FormTitle's empty .ft-chips
          placeholder (its load-time + Audit chips render in the toolbar via
          FormMetaContext) adds no empty inline line-box. Hidden while the grid is
          maximized (chrome-hide, mirrors ModuleListView). */}
      {!gridMax && (
        <div className="acc-top">
          <div className="acc-bc">
            <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
            <span>Settings</span><i className="ti ti-chevron-right" />
            <span>Accounting</span><i className="ti ti-chevron-right" />
            <span style={{ color: '#4a3728', fontWeight: 600 }}>Payment Terms Setup</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
            <ViewToggle view="list" onChange={(v) => { if (v === 'detail') toggleToDetail(); }} detailDisabled={ordered.length === 0} />
            <FormTitle pageId="acc-paymentterms-setup" loadMs={loadMs ?? null} loading={isFetching}
              auditFormName="Payment Terms Setup" />
          </div>
        </div>
      )}

      {/* pts-list tightens the gap between the search row and the grid card
          (owner request 2026-10-12) — see accounting.css .acc-body.pts-list. */}
      <div className="acc-body pts-list">
        {/* Action toolbar — assignment-only form: Refresh · Export only. No New /
            Delete (ALPACA parity), and no toolbar Edit (owner request 2026-10-14):
            editing is reached by the per-row Edit pencil, double-clicking a row, or
            the Detail View tab — so the toolbar Edit button was redundant. Hidden
            while the grid is maximized — the search row keeps Export + Restore. */}
        {!gridMax && (
          <AppToolbar
            onRefresh={() => void refetch()}
            refreshing={isFetching}
            exportData={{
              filename: 'PaymentTermsSetup',
              headers: ['Client', 'ClientCode', 'Address', 'Phone', 'Terms'],
              rows: visible.map((r) => [
                r.client ?? '', r.clientCode ?? '', r.address ?? '', r.phone ?? '',
                r.termDays != null ? `${r.termDays} days` : '',
              ]),
            }}
          />
        )}
        {/* Search + Export + Maximize on ONE row above the grid card (BO UI Standard /
            ModuleListView layout) — the grid's own header tools are hidden so the
            Export + Zoom controls are NOT a separate band inside the card. */}
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
          </div>
          <div className="mlv-grid-acts">
            <ExportMenu iconOnly triggerClassName="dv-gridact" filename="PaymentTermsSetup"
              headers={exportMatrix.headers} rows={exportMatrix.rows} />
            <button className={`dv-gridact${gridMax ? ' dv-on' : ''}`} onClick={() => setGridMax((v) => !v)}
              title={gridMax ? 'Restore' : 'Maximize'}>
              <i className={`ti ti-arrows-${gridMax ? 'minimize' : 'maximize'}`} />
            </button>
          </div>
        </div>
        {/* ONE white grid card — grid + its single footer only. Double-click a row
            → the Detail View (rules 10 & 12). */}
        <div className="acc-card">
          <AppDataGrid<PaymentTermsSetupRow>
            rows={visible}
            columns={columns}
            getRowId={(r) => r.id}
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            onRowDoubleClick={(r) => openDetail(r.id, 'view')}
            initialSort={{ key: 'address', dir: 'asc' }}
            loading={isFetching}
            emptyText="No clients found."
            hideHeaderTools
          />
        </div>
      </div>

      {options.length === 0 && (
        <div className="acc-hint">
          No payment-term values exist yet — add them under <strong>Settings ▸ Utility ▸ Accounting ▸
          Payment Terms</strong> (e.g. 30, 60, 90 days) so they appear in the Terms dropdown here, or
          use the <i className="ti ti-plus" /> button in the Terms editor.
        </div>
      )}

      {newTermPopup}
    </div>
  );
}
